/**
 * Argus — Pagină timeline URL visits per agent (Chrome/Edge/Firefox).
 * Bloc v1.0.9 #7 — 2026-05-07.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { fetchUrlEvents, type UrlEventItem } from '../lib/urlEvents';

const BROWSER_COLOR: Record<string, 'primary' | 'secondary' | 'warning' | 'info' | 'default'> = {
  chrome: 'primary',
  edge: 'info',
  brave: 'warning',
  firefox: 'secondary',
  opera: 'default',
};

export default function AgentUrlsPage(): JSX.Element {
  const { agentId } = useParams<{ agentId: string }>();
  const hex = (agentId ?? '').toUpperCase();
  const [search, setSearch] = useState('');
  const [cursor] = useState<number | undefined>(undefined);

  const query = useQuery({
    queryKey: ['url-events', hex, search, cursor],
    queryFn: () => {
      const q: { q?: string; cursor?: number } = {};
      if (search) q.q = search;
      if (cursor !== undefined) q.cursor = cursor;
      return fetchUrlEvents(hex, q);
    },
    refetchInterval: 30_000,
  });

  if (!hex) return <Alert severity="error">Missing agentId</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>URL visits — {hex}</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search URL"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 300 }}
        />
        <Button onClick={() => query.refetch()} variant="outlined">Refresh</Button>
      </Stack>

      {query.isLoading && <CircularProgress />}
      {query.isError && <Alert severity="error">{(query.error as Error).message}</Alert>}

      {query.data && (
        <Paper sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Browser</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Visited</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="caption" color="text.secondary">No URL events captured yet.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {query.data.items.map((item: UrlEventItem) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Chip size="small" label={item.browser} color={BROWSER_COLOR[item.browser] ?? 'default'} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Link href={item.url} target="_blank" rel="noopener noreferrer">{item.url}</Link>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.title ?? '—'}
                  </TableCell>
                  <TableCell>{new Date(item.visitedAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
